/**
 * The **Arena kill-streak combo counter** display (PRD #432): the `#arena-combo`
 * HUD element that renders the pure {@link ./comboTracker.ComboTracker}'s state -
 * the climbing `N×` number, the draining window underbar, and the tier styling
 * (`data-tier`) - and plays the two one-shot motions the machine can't: the
 * scale-punch **pop** on each increment and the hold-then-fall-away **break** on
 * expiry. All timing/tiering lives in the pure machine; this handle is a thin,
 * clock-free DOM reconciler driven from the render loop.
 *
 * Client-chrome discipline (the {@link ./arenaHud}/{@link ./arenaWarning} rules):
 * a plain-DOM sibling overlay of the canvas (markup/CSS in `index.html`),
 * `textContent` + `data-tier` + one inline bar width only, `pointer-events: none`
 * so a melee tap passes through, missing markup degrades every call to a no-op,
 * and `reset()` is the every-Welcome / run-end reconnect guard. The pop/break are
 * CSS keyframes re-triggered by the class-remove + forced-reflow + class-add
 * idiom (the {@link ./arenaHud} level-pop precedent) so they need no timers.
 */
import { comboTier, ARENA_COMBO_SHOW_AT, type ComboState } from './comboTracker';

/** The CSS class carrying the per-increment scale-punch keyframe (index.html). */
export const ARENA_COMBO_POP_CLASS = 'arena-combo-pop';

/** The CSS class carrying the on-expiry hold-then-fall-away keyframe (index.html). */
export const ARENA_COMBO_BREAK_CLASS = 'arena-combo-broke';

/** Options for {@link initArenaCombo}; `doc` is injected by tests. */
export interface ArenaComboOptions {
  /** The document to wire (defaults to the live `document`). */
  doc?: Document;
}

/** The handle `main.ts` drives the combo counter through. */
export interface ArenaComboHandle {
  /**
   * Reconcile the counter to a fresh {@link ComboState} (each render frame):
   * show + repaint the `N×`, the `data-tier`, and the draining bar while the
   * chain is visible (popping on a genuine increment), or hide it while it is
   * below the show-threshold - unless a break fall-away is still playing, which
   * owns the element until a new chain supersedes it or `reset()` clears it.
   */
  render(state: ComboState): void;
  /**
   * Play the chain's death (the sync-edge break): freeze the final `brokenCount`
   * and run the hold-then-fall-away. A sub-threshold chain (never shown) is a
   * silent no-op. Called once per break (the machine edge-triggers it).
   */
  breakChain(brokenCount: number): void;
  /** Hide + disarm immediately - the every-Welcome / run-end reconnect guard. */
  reset(): void;
}

/**
 * Wire the `#arena-combo` overlay (declared hidden in `index.html`). Every part
 * is looked up once; a missing root or part degrades to a no-op (the storm-board
 * resilience rule).
 */
export function initArenaCombo(opts: ArenaComboOptions = {}): ArenaComboHandle {
  const doc = opts.doc ?? document;
  const root = doc.getElementById('arena-combo');
  const countEl = doc.getElementById('arena-combo-count');
  const barFill = doc.getElementById('arena-combo-bar-fill');

  /** The last count rendered while visible - the pop's increment edge. */
  let shownCount = 0;
  /** Whether a break fall-away currently owns the element (render must not hide it). */
  let breaking = false;

  /** Re-trigger a one-shot CSS keyframe by remove + forced reflow + add. */
  const retrigger = (el: HTMLElement, cls: string): void => {
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  };

  return {
    render(state) {
      if (!root) return;
      if (state.visible) {
        // A live, shown chain supersedes any fall-away in progress.
        if (breaking) {
          breaking = false;
          root.classList.remove(ARENA_COMBO_BREAK_CLASS);
        }
        root.hidden = false;
        root.dataset.tier = String(state.tier);
        const text = `${state.count}×`;
        if (countEl && countEl.textContent !== text) countEl.textContent = text;
        // Pop only on a genuine increment - not on a bare windowFraction redraw.
        if (countEl && state.count > shownCount) retrigger(countEl as HTMLElement, ARENA_COMBO_POP_CLASS);
        shownCount = state.count;
        if (barFill) barFill.style.width = `${Math.round(state.windowFraction * 100)}%`;
      } else if (!breaking) {
        // Below the show-threshold and nothing falling away: idle/hidden.
        root.hidden = true;
        shownCount = 0;
      }
      // else: invisible but a break animation owns the element - leave it be.
    },
    breakChain(brokenCount) {
      shownCount = 0;
      // A chain that never reached the show-threshold was never on screen.
      if (!root || brokenCount < ARENA_COMBO_SHOW_AT) return;
      const text = `${brokenCount}×`;
      if (countEl) countEl.textContent = text;
      root.dataset.tier = String(comboTier(brokenCount));
      if (barFill) barFill.style.width = '0%';
      root.hidden = false;
      breaking = true;
      retrigger(root as HTMLElement, ARENA_COMBO_BREAK_CLASS);
    },
    reset() {
      breaking = false;
      shownCount = 0;
      if (root) {
        root.hidden = true;
        root.classList.remove(ARENA_COMBO_BREAK_CLASS);
        root.removeAttribute('data-tier');
      }
      if (countEl) countEl.classList.remove(ARENA_COMBO_POP_CLASS);
    },
  };
}
